import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../api/client";
import { ArrowLeft, MessageSquare, Send, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import Topbar from "../components/Topbar";
import { toast } from "sonner";

export default function Community() {
    const { courseId } = useParams();
    const { user } = useAuth();
    const [course, setCourse] = useState(null);
    const [threads, setThreads] = useState([]);
    const [openId, setOpenId] = useState(null);
    const [openThread, setOpenThread] = useState(null);
    const [loading, setLoading] = useState(true);
    const [newTitle, setNewTitle] = useState("");
    const [newBody, setNewBody] = useState("");
    const [reply, setReply] = useState("");

    const load = async () => {
        try {
            const [c, t] = await Promise.all([
                api.get(`/courses/${courseId}`),
                api.get(`/courses/${courseId}/discussions`),
            ]);
            setCourse(c);
            setThreads(t);
        } catch (e) {
            console.error(e);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [courseId]);

    const openDiscussion = async (id) => {
        setOpenId(id);
        const d = await api.get(`/discussions/${id}`);
        setOpenThread(d);
    };

    const createThread = async () => {
        if (!user) { toast.error("Sign in to post"); return; }
        if (!newTitle || !newBody) return;
        await api.post("/discussions", { course_id: courseId, title: newTitle, body: newBody });
        toast.success("Thread posted");
        setNewTitle(""); setNewBody("");
        load();
    };

    const sendReply = async () => {
        if (!reply || !openId) return;
        await api.post(`/discussions/${openId}/replies`, { body: reply });
        setReply("");
        openDiscussion(openId);
    };

    return (
        <div className="min-h-screen bg-ink-900 text-white">
            <Topbar searchPlaceholder="Search community..." />
            <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8">
                <Link to={`/catalog/${courseId}`} data-testid="back-course" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-6">
                    <ArrowLeft className="w-4 h-4" /> Back to course
                </Link>

                <div className="mb-8">
                    <span className="text-xs uppercase tracking-widest text-grape font-semibold">Community</span>
                    <h1 className="font-display font-black text-4xl tracking-tight mt-2">{course?.title || "Discussion"}</h1>
                    <p className="text-sm text-gray-400 mt-2">{threads.length} thread{threads.length !== 1 ? "s" : ""} from fellow operators.</p>
                </div>

                {/* New thread */}
                {user && (
                    <div className="bg-ink-700 rounded-3xl border border-white/5 p-6 mb-6">
                        <h3 className="font-display font-bold text-lg mb-4">Start a new thread</h3>
                        <input
                            data-testid="thread-title"
                            value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                            placeholder="Title — what's the question?"
                            className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-lime/40 mb-3"
                        />
                        <textarea
                            data-testid="thread-body"
                            value={newBody} onChange={(e) => setNewBody(e.target.value)}
                            rows={3} placeholder="Add details, context, or what you've tried so far…"
                            className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-lime/40 resize-none mb-4"
                        />
                        <button onClick={createThread} data-testid="post-thread" disabled={!newTitle || !newBody} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime text-ink-900 font-bold disabled:opacity-50 hover:bg-lime-hover transition-all">
                            <Send className="w-4 h-4" /> Post
                        </button>
                    </div>
                )}

                {/* Threads list */}
                {loading ? <Loader2 className="w-5 h-5 animate-spin text-lime" /> : threads.length === 0 ? (
                    <div className="bg-ink-700 rounded-3xl border border-white/5 p-10 text-center text-gray-400">
                        Be the first to start a discussion on this course.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {threads.map((t) => (
                            <div key={t.id} data-testid={`thread-${t.id}`} className="bg-ink-700 rounded-2xl border border-white/5 overflow-hidden">
                                <button onClick={() => openId === t.id ? (setOpenId(null), setOpenThread(null)) : openDiscussion(t.id)} className="w-full flex items-start gap-4 p-5 text-left hover:bg-ink-600/40 transition-colors">
                                    <img src={t.author_picture || "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop"} alt="" className="w-10 h-10 rounded-full object-cover" />
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-display font-bold leading-tight">{t.title}</h3>
                                        <p className="text-sm text-gray-400 mt-1 line-clamp-2">{t.body}</p>
                                        <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                                            <span>{t.author_name}</span>
                                            <span>•</span>
                                            <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {t.reply_count} replies</span>
                                        </div>
                                    </div>
                                </button>
                                {openId === t.id && openThread && (
                                    <div className="border-t border-white/5 p-5 bg-ink-800/40 space-y-3">
                                        {openThread.replies.map((r) => (
                                            <div key={r.id} className="flex items-start gap-3">
                                                <img src={r.author_picture || "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop"} alt="" className="w-8 h-8 rounded-full object-cover" />
                                                <div className="flex-1 bg-ink-700 rounded-xl px-4 py-2.5">
                                                    <div className="text-xs font-semibold">{r.author_name}</div>
                                                    <div className="text-sm text-gray-300 mt-0.5">{r.body}</div>
                                                </div>
                                            </div>
                                        ))}
                                        {user && (
                                            <div className="flex items-center gap-2 pt-2">
                                                <input data-testid="reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" className="flex-1 bg-ink-700 border border-white/5 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-lime/40" />
                                                <button onClick={sendReply} data-testid="send-reply" className="px-4 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold hover:bg-lime-hover">
                                                    <Send className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
